"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FinancialErrorCatalog = exports.FinancialErrorCodes = void 0;
exports.getFinancialErrorCatalogEntry = getFinancialErrorCatalogEntry;
/**
 * Financial-specific error codes — namespaced `PAYMENT_*`/`RECEIPT_*`/
 * `ADVANCE_DEPOSIT_*`/`REFUND_*`/`TARIFF_*`/`PACKAGE_*`/`DISCOUNT_*`/
 * `BILLING_*`, additive and parallel to backend's own
 * `ErrorCodes`/`ErrorCatalog` (`backend/src/shared/constants/
 * error-codes.constant.ts`, `backend/src/core/exception/error-catalog.ts`),
 * which this package does not import or modify — same discipline
 * `patient-error-codes.ts`/`appointment-error-codes.ts` already
 * established (2 real precedents, not a new pattern). Each code here
 * maps to a real, named business rule already enforced inside a
 * frozen Financial Domain Service (Stage 5C.2) — this file does not
 * invent new rules, only gives ones that already exist a stable,
 * documented code a client can branch on.
 *
 * Collision check (verified against both existing registries before
 * adding these): Patient uses `PATIENT_*`/`MERGE_*`/`COVERAGE_*`/
 * `CONSENT_*`/`PROTECTION_*`/`IDENTITY_*`; Appointment uses
 * `APPOINTMENT_*`/`CHECK_IN_*`/`DOCTOR_*`/`QUEUE_*`/`REMINDER_*`/
 * `SLOT_HOLD_*`/`TELEMEDICINE_*`/`WAITLIST_*`. None of the prefixes
 * below overlap either list — re-verified by the collision-guard unit
 * test in `tests/errors/financial-error-codes.test.ts` (contracts
 * package), which fails loudly if a future edit introduces one.
 */
exports.FinancialErrorCodes = {
    PAYMENT_SPLIT_SUM_MISMATCH: 'PAYMENT_SPLIT_SUM_MISMATCH',
    PAYMENT_NOT_COMPLETED: 'PAYMENT_NOT_COMPLETED',
    PAYMENT_OPTIMISTIC_LOCK_CONFLICT: 'PAYMENT_OPTIMISTIC_LOCK_CONFLICT',
    RECEIPT_ALREADY_CANCELLED: 'RECEIPT_ALREADY_CANCELLED',
    RECEIPT_ALREADY_REFUNDED: 'RECEIPT_ALREADY_REFUNDED',
    RECEIPT_OPTIMISTIC_LOCK_CONFLICT: 'RECEIPT_OPTIMISTIC_LOCK_CONFLICT',
    ADVANCE_DEPOSIT_INSUFFICIENT_BALANCE: 'ADVANCE_DEPOSIT_INSUFFICIENT_BALANCE',
    ADVANCE_DEPOSIT_ACCOUNT_NOT_ACTIVE: 'ADVANCE_DEPOSIT_ACCOUNT_NOT_ACTIVE',
    ADVANCE_DEPOSIT_SETTLEMENT_NOT_APPLICABLE: 'ADVANCE_DEPOSIT_SETTLEMENT_NOT_APPLICABLE',
    ADVANCE_DEPOSIT_NON_ZERO_BALANCE: 'ADVANCE_DEPOSIT_NON_ZERO_BALANCE',
    ADVANCE_DEPOSIT_OPTIMISTIC_LOCK_CONFLICT: 'ADVANCE_DEPOSIT_OPTIMISTIC_LOCK_CONFLICT',
    REFUND_EXCEEDS_ORIGINAL_PAYMENT: 'REFUND_EXCEEDS_ORIGINAL_PAYMENT',
    REFUND_ORIGINAL_PAYMENT_NOT_COMPLETED: 'REFUND_ORIGINAL_PAYMENT_NOT_COMPLETED',
    TARIFF_NOT_DRAFT: 'TARIFF_NOT_DRAFT',
    TARIFF_HAS_NO_LINES: 'TARIFF_HAS_NO_LINES',
    TARIFF_OVERLAPPING_ACTIVE_WINDOW: 'TARIFF_OVERLAPPING_ACTIVE_WINDOW',
    PACKAGE_NOT_DRAFT: 'PACKAGE_NOT_DRAFT',
    PACKAGE_HAS_NO_INCLUDED_ITEMS: 'PACKAGE_HAS_NO_INCLUDED_ITEMS',
    DISCOUNT_RULE_INACTIVE: 'DISCOUNT_RULE_INACTIVE',
    DISCOUNT_RULE_NOT_EFFECTIVE: 'DISCOUNT_RULE_NOT_EFFECTIVE',
    DISCOUNT_REDEMPTION_LIMIT_REACHED: 'DISCOUNT_REDEMPTION_LIMIT_REACHED',
    BILLING_CHARGE_NOT_IN_TARIFF: 'BILLING_CHARGE_NOT_IN_TARIFF',
    BILLING_CHARGE_INACTIVE: 'BILLING_CHARGE_INACTIVE',
    VALIDATION_ERROR: 'FINANCIAL_VALIDATION_ERROR',
};
exports.FinancialErrorCatalog = {
    [exports.FinancialErrorCodes.PAYMENT_SPLIT_SUM_MISMATCH]: {
        httpStatus: 400,
        description: 'Payment line amounts do not sum exactly to the payment total (TD-5A-2).',
        retryable: false,
    },
    [exports.FinancialErrorCodes.PAYMENT_NOT_COMPLETED]: {
        httpStatus: 409,
        description: 'The referenced payment is not COMPLETED — this operation requires it.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.PAYMENT_OPTIMISTIC_LOCK_CONFLICT]: {
        httpStatus: 409,
        description: 'The payment was modified by another user since it was last read.',
        retryable: true,
    },
    [exports.FinancialErrorCodes.RECEIPT_ALREADY_CANCELLED]: {
        httpStatus: 409,
        description: 'This receipt is already CANCELLED — it cannot be cancelled again.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.RECEIPT_ALREADY_REFUNDED]: {
        httpStatus: 409,
        description: 'This receipt already has a linked refund payment — a receipt can only be refunded once.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.RECEIPT_OPTIMISTIC_LOCK_CONFLICT]: {
        httpStatus: 409,
        description: 'The receipt was modified by another user since it was last read.',
        retryable: true,
    },
    [exports.FinancialErrorCodes.ADVANCE_DEPOSIT_INSUFFICIENT_BALANCE]: {
        httpStatus: 400,
        description: 'The requested debit exceeds the account’s current balance.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.ADVANCE_DEPOSIT_ACCOUNT_NOT_ACTIVE]: {
        httpStatus: 409,
        description: 'This account is not ACTIVE — credit/debit/settle require it to be.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.ADVANCE_DEPOSIT_SETTLEMENT_NOT_APPLICABLE]: {
        httpStatus: 409,
        description: 'Only IPD_DEPOSIT/CORPORATE_DEPOSIT accounts can be settled — OPD_ADVANCE accounts are drawn down via debit.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.ADVANCE_DEPOSIT_NON_ZERO_BALANCE]: {
        httpStatus: 409,
        description: 'An account with a non-zero balance cannot be closed.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.ADVANCE_DEPOSIT_OPTIMISTIC_LOCK_CONFLICT]: {
        httpStatus: 409,
        description: 'The account was modified by another user since it was last read.',
        retryable: true,
    },
    [exports.FinancialErrorCodes.REFUND_EXCEEDS_ORIGINAL_PAYMENT]: {
        httpStatus: 400,
        description: 'The requested refund amount exceeds the original payment total (TD-5A-3).',
        retryable: false,
    },
    [exports.FinancialErrorCodes.REFUND_ORIGINAL_PAYMENT_NOT_COMPLETED]: {
        httpStatus: 409,
        description: 'The original payment is not COMPLETED — it cannot be refunded.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.TARIFF_NOT_DRAFT]: {
        httpStatus: 409,
        description: 'Only a DRAFT tariff can be activated or have lines added.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.TARIFF_HAS_NO_LINES]: {
        httpStatus: 409,
        description: 'An empty tariff (zero lines) cannot be activated.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.TARIFF_OVERLAPPING_ACTIVE_WINDOW]: {
        httpStatus: 409,
        description: 'An ACTIVE tariff already covers this payer/branch with an overlapping effective window.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.PACKAGE_NOT_DRAFT]: {
        httpStatus: 409,
        description: 'Only a DRAFT package can be activated.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.PACKAGE_HAS_NO_INCLUDED_ITEMS]: {
        httpStatus: 409,
        description: 'A package with zero INCLUDED items cannot be activated.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.DISCOUNT_RULE_INACTIVE]: {
        httpStatus: 409,
        description: 'This discount rule is inactive.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.DISCOUNT_RULE_NOT_EFFECTIVE]: {
        httpStatus: 409,
        description: 'This discount rule is outside its effective window.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.DISCOUNT_REDEMPTION_LIMIT_REACHED]: {
        httpStatus: 409,
        description: 'This coupon-gated discount rule has reached its redemption limit.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.BILLING_CHARGE_NOT_IN_TARIFF]: {
        httpStatus: 400,
        description: 'The specified charge is not present in the specified tariff — no fallback price is used when a tariff is explicitly requested.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.BILLING_CHARGE_INACTIVE]: {
        httpStatus: 409,
        description: 'The specified charge is inactive, soft-deleted, or outside its effective window.',
        retryable: false,
    },
    [exports.FinancialErrorCodes.VALIDATION_ERROR]: {
        httpStatus: 422,
        description: 'Request body failed schema validation.',
        retryable: false,
    },
};
function getFinancialErrorCatalogEntry(code) {
    return exports.FinancialErrorCatalog[code];
}
//# sourceMappingURL=financial-error-codes.js.map